/**
 * DeviceList Component
 * Lista virtualizada de dispositivos com otimizações de performance
 * Suporta atualizações em tempo real via WebSocket
 *
 * 2ECOMP: Otimizado para 3000+ dispositivos
 * 2ECOMP Fase 3: Keyboard navigation e acessibilidade
 * 2ECOMP: PremiumUI Refresh — altura fixa 48px, sem expand
 * 2ECOMP: PremiumUI Refresh — group headers com colapso persistido
 */

import {
  useRef, useMemo, useCallback, useEffect, memo, useState,
} from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useVirtualizer } from '@tanstack/react-virtual';
import { makeStyles } from 'tss-react/mui';
import {
  Box, Typography, IconButton, Checkbox, Menu, MenuItem, ListItemIcon, ListItemText,
  InputBase,
} from '@mui/material';
import {
  DirectionsCar, SearchOff, ChevronRight, MoreVert,
  Add, Edit, DeleteOutline,
} from '@mui/icons-material';
import DeviceRow from './DeviceRow';
import GroupEditModal from './components/GroupEditModal';
import { devicesActions } from '../store';
import { groupsActions } from '../store/groups';
import { useTranslation } from '../common/components/LocalizationProvider';
import { usePremiumTokens } from '../common/theme/usePremiumTokens';
import usePersistedState from '../common/util/usePersistedState';
// 2ECOMP: PremiumUI Refresh — constantes de layout centralizadas
import {
  DEVICE_LIST_WIDTH,
  ICON_BAR_WIDTH,
  DEVICE_ROW_HEIGHT,
  DEVICE_GROUP_HEIGHT,
  Z_INDEX,
  SIDEBAR_TRANSITION,
} from '../common/theme/layout';
// 2ECOMP: PremiumUI Refresh — TOPBAR_HEIGHT não mais usado (TopBar oculta na MainPage)

// 2ECOMP: PremiumUI Refresh — group header compacto (40px) com menu inline
const GroupHeader = memo(({
  name, count, isExpanded, onToggle, groupId,
  selectionState, onCheckboxChange, style, className,
  onRename, onDelete, onEdit, onCreateGroup, t,
}) => {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(name);

  // 2ECOMP: PremiumUI Refresh — groupId === 0 = "Sem grupo"
  const isReal = groupId !== 0;

  const handleMenuOpen = useCallback((e) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuAnchor(null);
  }, []);

  const handleRenameStart = useCallback(() => {
    setMenuAnchor(null);
    setEditValue(name);
    setEditing(true);
  }, [name]);

  const handleRenameSubmit = useCallback(() => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== name) {
      onRename(groupId, trimmed);
    }
    setEditing(false);
  }, [editValue, name, onRename, groupId]);

  const handleRenameKeyDown = useCallback((e) => {
    e.stopPropagation();
    if (e.key === 'Enter') {
      handleRenameSubmit();
    } else if (e.key === 'Escape') {
      setEditing(false);
    }
  }, [handleRenameSubmit]);

  const handleDeleteClick = useCallback(() => {
    setMenuAnchor(null);
    onDelete(groupId, name);
  }, [onDelete, groupId, name]);

  const handleEditClick = useCallback(() => {
    setMenuAnchor(null);
    if (onEdit) onEdit(groupId);
  }, [onEdit, groupId]);

  const handleCreateClick = useCallback(() => {
    setMenuAnchor(null);
    if (onCreateGroup) onCreateGroup();
  }, [onCreateGroup]);

  return (
    <div className={className} style={style}>
      <Box
        onClick={onToggle}
        role="button"
        aria-expanded={isExpanded}
        aria-label={`${name}, ${count} dispositivos`}
        sx={{
          height: DEVICE_GROUP_HEIGHT,
          display: 'flex',
          alignItems: 'center',
          px: 1,
          gap: 0.5,
          cursor: 'pointer',
          backgroundColor: (theme) => (theme.palette.mode === 'dark'
            ? 'rgba(255,255,255,0.03)'
            : 'rgba(0,0,0,0.02)'),
          '&:hover': { backgroundColor: 'action.hover' },
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <ChevronRight
          sx={{
            fontSize: 16,
            transition: 'transform 200ms ease',
            transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
            color: 'text.secondary',
            flexShrink: 0,
          }}
        />
        <Checkbox
          size="small"
          sx={{ p: 0.25 }}
          onClick={(e) => e.stopPropagation()}
          onChange={onCheckboxChange}
          checked={selectionState === 'all'}
          indeterminate={selectionState === 'some'}
        />
        {editing ? (
          <InputBase
            autoFocus
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleRenameSubmit}
            onKeyDown={handleRenameKeyDown}
            onClick={(e) => e.stopPropagation()}
            sx={{
              flex: 1,
              fontSize: '0.72rem',
              fontWeight: 600,
              color: 'text.secondary',
              '& input': { p: 0 },
            }}
          />
        ) : (
          <Typography
            variant="caption"
            noWrap
            sx={{ flex: 1, fontWeight: 600, fontSize: '0.72rem', color: 'text.secondary' }}
          >
            {name}
          </Typography>
        )}
        <Typography
          variant="caption"
          sx={{
            color: 'text.disabled', fontSize: '0.68rem', minWidth: 20, textAlign: 'right', mr: 0.5,
          }}
        >
          {count}
        </Typography>
        {/* 2ECOMP: PremiumUI Refresh — menu ⋮ para todos os grupos */}
        <IconButton
          size="small"
          sx={{ p: 0.25 }}
          onClick={handleMenuOpen}
        >
          <MoreVert sx={{ fontSize: 14 }} />
        </IconButton>
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
          onClick={(e) => e.stopPropagation()}
          slotProps={{ paper: { sx: { minWidth: 150 } } }}
        >
          {/* 2ECOMP: PremiumUI Refresh — "Sem grupo" só mostra "Criar grupo" */}
          {!isReal && (
            <MenuItem onClick={handleCreateClick} dense>
              <ListItemIcon sx={{ minWidth: '28px !important', '& svg': { fontSize: 16 } }}>
                <Add />
              </ListItemIcon>
              <ListItemText>{t('groupCreate') || 'Novo grupo'}</ListItemText>
            </MenuItem>
          )}
          {/* 2ECOMP: PremiumUI Refresh — grupos reais: Editar, Renomear, Excluir */}
          {isReal && [
            <MenuItem key="edit" onClick={handleEditClick} dense>
              <ListItemIcon sx={{ minWidth: '28px !important', '& svg': { fontSize: 16 } }}>
                <Edit />
              </ListItemIcon>
              <ListItemText>{t('sharedEdit') || 'Editar'}</ListItemText>
            </MenuItem>,
            <MenuItem key="rename" onClick={handleRenameStart} dense>
              <ListItemIcon sx={{ minWidth: '28px !important', '& svg': { fontSize: 16 } }}>
                <Edit />
              </ListItemIcon>
              <ListItemText>{t('groupRename') || 'Renomear'}</ListItemText>
            </MenuItem>,
            <MenuItem key="delete" onClick={handleDeleteClick} dense>
              <ListItemIcon sx={{ minWidth: '28px !important', color: 'error.main', '& svg': { fontSize: 16 } }}>
                <DeleteOutline />
              </ListItemIcon>
              <ListItemText sx={{ '& .MuiTypography-root': { color: 'error.main' } }}>
                {t('groupDelete') || 'Excluir'}
              </ListItemText>
            </MenuItem>,
          ]}
        </Menu>
      </Box>
    </div>
  );
});

// 2ECOMP: PremiumUI Refresh — VirtualRow simplificado (sem expand)
const VirtualRow = memo(({
  device, position, style, className,
  isSelected, rowRef, index, totalCount, groups,
  isHidden, onToggleVisibility,
}) => (
  <div
    ref={isSelected ? rowRef : null}
    id={`device-row-${device?.id}`}
    className={className}
    style={style}
    role="option"
    aria-selected={isSelected}
    aria-setsize={totalCount}
    aria-posinset={index + 1}
    data-device-id={device?.id}
    tabIndex={isSelected ? 0 : -1}
  >
    <DeviceRow
      device={device}
      position={position}
      isSelected={isSelected}
      groups={groups}
      isHidden={isHidden}
      onToggleVisibility={onToggleVisibility}
    />
  </div>
), (prevProps, nextProps) => {
  if (prevProps.device !== nextProps.device) return false;
  if (prevProps.isSelected !== nextProps.isSelected) return false;
  if (prevProps.groups !== nextProps.groups) return false;
  if (prevProps.isHidden !== nextProps.isHidden) return false;
  if (prevProps.onToggleVisibility !== nextProps.onToggleVisibility) return false;
  return (
    prevProps.position === nextProps.position
    && prevProps.style.transform === nextProps.style.transform
  );
});

const useStyles = makeStyles()((theme, { tokens, isOpen }) => ({
  // 2ECOMP: PremiumUI Refresh — painel lateral fixo quando standalone
  panelRoot: {
    position: 'fixed',
    left: ICON_BAR_WIDTH,
    top: 0, // 2ECOMP: PremiumUI Refresh — TopBar oculta na MainPage
    bottom: 0,
    width: DEVICE_LIST_WIDTH,
    zIndex: Z_INDEX.DEVICE_LIST,
    transform: isOpen ? 'translateX(0)' : `translateX(-${DEVICE_LIST_WIDTH}px)`,
    transition: SIDEBAR_TRANSITION,
    backgroundColor: theme.palette.background.paper,
    borderRight: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  container: {
    flex: 1,
    width: '100%',
    overflow: 'auto',
    willChange: 'transform',
    minHeight: 0,
    WebkitOverflowScrolling: 'touch',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    padding: theme.spacing(4),
    textAlign: 'center',
    animation: '$fadeIn 0.3s ease-out',
  },
  emptyIcon: {
    fontSize: 64,
    color: tokens?.text?.muted || theme.palette.text.disabled,
    marginBottom: theme.spacing(2),
    opacity: 0.5,
  },
  emptyTitle: {
    fontSize: '1rem',
    fontWeight: 600,
    color: tokens?.text?.secondary || theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  emptySubtitle: {
    fontSize: '0.85rem',
    color: tokens?.text?.muted || theme.palette.text.disabled,
    maxWidth: 280,
  },
  '@keyframes fadeIn': {
    '0%': { opacity: 0, transform: 'translateY(10px)' },
    '100%': { opacity: 1, transform: 'translateY(0)' },
  },
  virtualRow: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    willChange: 'transform',
    contain: 'layout style',
    '&:focus-visible': {
      outline: `2px solid ${tokens?.accent?.primary || theme.palette.primary.main}`,
      outlineOffset: -2,
      zIndex: 10,
    },
  },
  listContainer: {
    width: '100%',
    position: 'relative',
    '&:focus': {
      outline: 'none',
    },
  },
}));

// 2ECOMP: PremiumUI Refresh — isOpen/standalone props para painel lateral
const DeviceList = ({ devices = [], hasActiveFilter = false, isOpen = true, standalone = false }) => {
  const tokens = usePremiumTokens();
  const { classes } = useStyles({ tokens, isOpen });
  const containerRef = useRef(null);
  const selectedRowRef = useRef(null);
  const t = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // 2ECOMP Fase 3: Device selecionado do Redux
  const selectedId = useSelector((state) => state.devices.selectedId);

  // Posições dos dispositivos do Redux (atualizam em tempo real)
  const positions = useSelector((state) => state.session.positions, shallowEqual);

  // 2ECOMP: PremiumUI Refresh — grupos do Redux
  const reduxGroups = useSelector((state) => state.groups.items, shallowEqual);

  // 2ECOMP: PremiumUI Refresh — devices ocultos no mapa
  const hiddenDeviceIds = useSelector((state) => state.devices.hiddenDeviceIds);
  const hiddenSet = useMemo(
    () => new Set(hiddenDeviceIds),
    [hiddenDeviceIds],
  );
  // 2ECOMP: performance-fix — refs para callback estável (evita 3000 re-renders por toggle)
  const selectedIdRef = useRef(selectedId);
  const hiddenSetRef = useRef(hiddenSet);
  useEffect(() => { selectedIdRef.current = selectedId; }, [selectedId]);
  useEffect(() => { hiddenSetRef.current = hiddenSet; }, [hiddenSet]);

  const handleToggleVisibility = useCallback((deviceId) => {
    dispatch(devicesActions.toggleDeviceVisibility(deviceId));
    // 2ECOMP: PremiumUI Refresh — deselecionar se ocultar o device selecionado
    if (deviceId === selectedIdRef.current && !hiddenSetRef.current.has(deviceId)) {
      dispatch(devicesActions.selectId(null));
    }
  }, [dispatch]);

  // 2ECOMP: grupos inline — lista memoizada para passar como prop ao DeviceRow
  const groupsList = useMemo(
    () => Object.values(reduxGroups || {}).sort((a, b) => a.name.localeCompare(b.name)),
    [reduxGroups],
  );

  // 2ECOMP: PremiumUI Refresh — colapso de grupos persistido
  const [collapsedGroups, setCollapsedGroups] = usePersistedState('collapsedGroups', {});

  const toggleGroup = useCallback((groupId) => {
    setCollapsedGroups((prev) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  }, [setCollapsedGroups]);

  const isGroupExpanded = useCallback(
    (groupId) => !collapsedGroups[groupId],
    [collapsedGroups],
  );

  // Memoizar a lista de dispositivos
  const safeDevices = useMemo(
    () => (Array.isArray(devices) ? devices : []),
    [devices],
  );

  // 2ECOMP: PremiumUI Refresh — verificar se há grupos para agrupar
  const hasGroups = useMemo(
    () => Object.keys(reduxGroups).length > 0,
    [reduxGroups],
  );

  // 2ECOMP: PremiumUI Refresh — flat list com grupos intercalados
  const flatItems = useMemo(() => {
    if (!hasGroups) {
      // Sem grupos: lista flat simples (retrocompatível)
      return safeDevices.map((device) => ({
        type: 'device',
        id: device.id,
        device,
      }));
    }

    const items = [];
    const devicesByGroup = new Map();
    const ungrouped = [];

    // 2ECOMP: PremiumUI Refresh — devices com groupId órfão (grupo deletado) vão para "Sem Grupo"
    safeDevices.forEach((device) => {
      if (device.groupId && reduxGroups[device.groupId]) {
        if (!devicesByGroup.has(device.groupId)) {
          devicesByGroup.set(device.groupId, []);
        }
        devicesByGroup.get(device.groupId).push(device);
      } else {
        ungrouped.push(device);
      }
    });

    // 2ECOMP: PremiumUI Refresh — "Sem grupo" sempre no topo
    if (ungrouped.length > 0) {
      items.push({
        type: 'group',
        id: 'group-0',
        groupId: 0,
        name: t('groupNoGroup') || 'Sem grupo',
        count: ungrouped.length,
      });
      if (isGroupExpanded(0)) {
        ungrouped.forEach((device) => {
          items.push({ type: 'device', id: device.id, device });
        });
      }
    }

    // 2ECOMP: PremiumUI Refresh — grupos ordenados por device count desc
    const sortedGroups = Object.values(reduxGroups)
      .filter((g) => devicesByGroup.has(g.id))
      .sort((a, b) => {
        const countDiff = (devicesByGroup.get(b.id)?.length || 0)
          - (devicesByGroup.get(a.id)?.length || 0);
        if (countDiff !== 0) return countDiff;
        return a.name.localeCompare(b.name); // desempate alfabético
      });

    sortedGroups.forEach((group) => {
      const groupDevices = devicesByGroup.get(group.id) || [];
      items.push({
        type: 'group',
        id: `group-${group.id}`,
        groupId: group.id,
        name: group.name,
        count: groupDevices.length,
      });
      if (isGroupExpanded(group.id)) {
        groupDevices.forEach((device) => {
          items.push({ type: 'device', id: device.id, device });
        });
      }
    });

    return items;
  }, [safeDevices, reduxGroups, hasGroups, isGroupExpanded, t]);

  // 2ECOMP: PremiumUI Refresh — índices dos devices no flatItems (para keyboard nav)
  const deviceIndices = useMemo(
    () => flatItems.reduce((acc, item, index) => {
      if (item.type === 'device') acc.push(index);
      return acc;
    }, []),
    [flatItems],
  );

  // 2ECOMP Fase 3: Índice flat do device selecionado
  const selectedFlatIndex = useMemo(() => {
    if (!selectedId) return -1;
    return flatItems.findIndex((item) => item.type === 'device' && item.id === selectedId);
  }, [selectedId, flatItems]);

  // 2ECOMP Fase 3: Handler para selecionar device por índice flat
  const selectDeviceByFlatIndex = useCallback((flatIndex) => {
    const item = flatItems[flatIndex];
    if (item && item.type === 'device') {
      dispatch(devicesActions.selectId(item.id));
    }
  }, [flatItems, dispatch]);

  // 2ECOMP: PremiumUI Refresh — helper para filtrar devices de um grupo
  // groupId=0 ("Sem Grupo") inclui devices sem grupo E órfãos (grupo deletado)
  const getDevicesForGroup = useCallback((groupId) => safeDevices.filter(
    (d) => (groupId === 0
      ? (!d.groupId || !reduxGroups[d.groupId])
      : d.groupId === groupId),
  ), [safeDevices, reduxGroups]);

  // 2ECOMP: PremiumUI Refresh — smart checkbox igual GPSWOX
  // Retorna: 'all' (checked) | 'some' (indeterminate) | 'none' (unchecked)
  const getGroupVisibilityState = useCallback((groupId) => {
    const groupDevices = getDevicesForGroup(groupId);
    if (groupDevices.length === 0) return 'none';
    const visibleCount = groupDevices.filter((d) => !hiddenSet.has(d.id)).length;
    if (visibleCount === groupDevices.length) return 'all';
    if (visibleCount === 0) return 'none';
    return 'some';
  }, [getDevicesForGroup, hiddenSet]);

  // 2ECOMP: PremiumUI Refresh — checkbox grupo = toggle visibilidade (igual GPSWOX)
  const handleGroupVisibilityToggle = useCallback((groupId) => {
    const deviceIds = getDevicesForGroup(groupId).map((d) => d.id);
    if (deviceIds.length === 0) return;
    // 2ECOMP: PremiumUI Refresh — se vai ocultar (todos visíveis) e selected está no grupo, deselecionar
    const allVisible = deviceIds.every((id) => !hiddenSet.has(id));
    if (allVisible && selectedId && deviceIds.includes(selectedId)) {
      dispatch(devicesActions.selectId(null));
    }
    dispatch(devicesActions.toggleGroupVisibility({ deviceIds }));
  }, [getDevicesForGroup, dispatch, hiddenSet, selectedId]);

  // 2ECOMP: PremiumUI Refresh — group modal state
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null); // null = create, { id, name } = edit

  const handleOpenCreateGroup = useCallback(() => {
    setEditingGroup(null);
    setGroupModalOpen(true);
  }, []);

  const handleOpenEditGroup = useCallback((groupId) => {
    const group = reduxGroups[groupId];
    if (group) {
      setEditingGroup({ id: group.id, name: group.name });
      setGroupModalOpen(true);
    }
  }, [reduxGroups]);

  const handleCloseGroupModal = useCallback(() => {
    setGroupModalOpen(false);
    setEditingGroup(null);
  }, []);

  const handleRenameGroup = useCallback(async (groupId, newName) => {
    const group = reduxGroups[groupId];
    if (!group) return;
    try {
      const response = await fetch(`/api/groups/${groupId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...group, name: newName }),
      });
      if (response.ok) {
        const updated = await response.json();
        dispatch(groupsActions.update(updated));
      }
    } catch {
      // silently fail
    }
  }, [reduxGroups, dispatch]);

  const handleDeleteGroup = useCallback(async (groupId) => {
    if (!window.confirm(t('groupDeleteConfirm') || 'Excluir este grupo? Os dispositivos serão movidos para "Sem grupo".')) return;
    try {
      const response = await fetch(`/api/groups/${groupId}`, { method: 'DELETE' });
      if (response.ok) {
        dispatch(groupsActions.remove(groupId));
        // Devices in this group lose their groupId — refresh devices
        const devResponse = await fetch('/api/devices');
        if (devResponse.ok) {
          const devicesData = await devResponse.json();
          dispatch(devicesActions.update(devicesData));
        }
      }
    } catch {
      // silently fail
    }
  }, [dispatch, t]);

  // 2ECOMP Fase 3: Keyboard navigation handler
  const handleKeyDown = useCallback((e) => {
    const tagName = e.target.tagName.toLowerCase();
    if (tagName === 'input' || tagName === 'textarea' || e.target.isContentEditable) {
      return;
    }
    if (deviceIndices.length === 0) return;

    const currentDevicePos = deviceIndices.indexOf(selectedFlatIndex);

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (currentDevicePos < deviceIndices.length - 1) {
          selectDeviceByFlatIndex(deviceIndices[currentDevicePos + 1]);
        } else if (currentDevicePos === -1 && deviceIndices.length > 0) {
          selectDeviceByFlatIndex(deviceIndices[0]);
        }
        break;

      case 'ArrowUp':
        e.preventDefault();
        if (currentDevicePos > 0) {
          selectDeviceByFlatIndex(deviceIndices[currentDevicePos - 1]);
        } else if (currentDevicePos === -1 && deviceIndices.length > 0) {
          selectDeviceByFlatIndex(deviceIndices[deviceIndices.length - 1]);
        }
        break;

      case 'Home':
        e.preventDefault();
        selectDeviceByFlatIndex(deviceIndices[0]);
        break;

      case 'End':
        e.preventDefault();
        selectDeviceByFlatIndex(deviceIndices[deviceIndices.length - 1]);
        break;

      // Atalhos rápidos
      case 'm':
      case 'M':
        if (selectedId && !e.ctrlKey && !e.metaKey) {
          e.preventDefault();
          dispatch(devicesActions.selectId(selectedId));
        }
        break;

      case 'h':
      case 'H':
        if (selectedId && !e.ctrlKey && !e.metaKey) {
          e.preventDefault();
          navigate(`/replay?deviceId=${selectedId}`);
        }
        break;

      case 'e':
      case 'E':
        if (selectedId && !e.ctrlKey && !e.metaKey) {
          e.preventDefault();
          navigate(`/settings/device/${selectedId}`);
        }
        break;

      default:
        break;
    }
  }, [deviceIndices, selectedFlatIndex, selectedId, selectDeviceByFlatIndex, dispatch, navigate]);

  // 2ECOMP Fase 3: Attach keyboard listener
  useEffect(() => {
    const handleGlobalKeyDown = (e) => handleKeyDown(e);
    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, [handleKeyDown]);

  // 2ECOMP: PremiumUI Refresh — height misto grupos + devices
  const rowVirtualizer = useVirtualizer({
    count: flatItems.length,
    getScrollElement: () => containerRef.current,
    estimateSize: useCallback((index) => {
      const item = flatItems[index];
      return item?.type === 'group' ? DEVICE_GROUP_HEIGHT : DEVICE_ROW_HEIGHT;
    }, [flatItems]),
    overscan: 15,
    getItemKey: useCallback((index) => flatItems[index]?.id ?? index, [flatItems]),
  });

  // 2ECOMP Fase 3: Scroll to selected item quando seleção muda via keyboard
  const prevSelectedFlatIndexRef = useRef(selectedFlatIndex);
  useEffect(() => {
    if (selectedFlatIndex >= 0 && selectedFlatIndex !== prevSelectedFlatIndexRef.current) {
      rowVirtualizer.scrollToIndex(selectedFlatIndex, {
        align: 'auto',
        behavior: 'smooth',
      });
    }
    prevSelectedFlatIndexRef.current = selectedFlatIndex;
  }, [selectedFlatIndex, rowVirtualizer]);

  // Track item count para detectar mudanças de filtro
  const prevCountRef = useRef(0);

  useEffect(() => {
    if (prevCountRef.current > 0 && prevCountRef.current !== flatItems.length) {
      if (containerRef.current) {
        containerRef.current.scrollTop = 0;
      }
    }
    prevCountRef.current = flatItems.length;
  }, [flatItems.length]);

  // Empty state
  if (safeDevices.length === 0) {
    const emptyContent = (
      <Box className={classes.emptyState}>
        {hasActiveFilter ? (
          <>
            <SearchOff className={classes.emptyIcon} />
            <Typography className={classes.emptyTitle}>
              {t('sharedNoResults') || 'Nenhum resultado encontrado'}
            </Typography>
            <Typography className={classes.emptySubtitle}>
              {t('sharedNoResultsHint') || 'Tente ajustar os filtros ou busca para encontrar dispositivos'}
            </Typography>
          </>
        ) : (
          <>
            <DirectionsCar className={classes.emptyIcon} />
            <Typography className={classes.emptyTitle}>
              {t('sharedNoDevices') || 'Nenhum dispositivo'}
            </Typography>
            <Typography className={classes.emptySubtitle}>
              {t('sharedNoDevicesHint') || 'Adicione dispositivos para começar a rastrear sua frota'}
            </Typography>
          </>
        )}
      </Box>
    );
    if (standalone) {
      return <div className={classes.panelRoot}>{emptyContent}</div>;
    }
    return emptyContent;
  }

  const virtualItems = rowVirtualizer.getVirtualItems();
  const deviceCount = deviceIndices.length;

  const listContent = (
    <div
      ref={containerRef}
      className={classes.container}
      role="listbox"
      aria-label={t('deviceTitle') || 'Lista de dispositivos'}
      aria-activedescendant={selectedId ? `device-row-${selectedId}` : undefined}
      tabIndex={0}
    >
      <div
        className={classes.listContainer}
        style={{ height: rowVirtualizer.getTotalSize() }}
      >
        {virtualItems.map((virtualRow) => {
          const item = flatItems[virtualRow.index];
          if (!item) return null;

          // 2ECOMP: PremiumUI Refresh — renderização condicional grupo/device
          if (item.type === 'group') {
            return (
              <GroupHeader
                key={item.id}
                name={item.name}
                count={item.count}
                groupId={item.groupId}
                isExpanded={isGroupExpanded(item.groupId)}
                onToggle={() => toggleGroup(item.groupId)}
                selectionState={getGroupVisibilityState(item.groupId)}
                onCheckboxChange={() => handleGroupVisibilityToggle(item.groupId)}
                onRename={handleRenameGroup}
                onDelete={handleDeleteGroup}
                onEdit={handleOpenEditGroup}
                onCreateGroup={handleOpenCreateGroup}
                t={t}
                className={classes.virtualRow}
                style={{
                  height: virtualRow.size,
                  transform: `translateY(${virtualRow.start}px)`,
                }}
              />
            );
          }

          const isDeviceSelected = item.id === selectedId;
          return (
            <VirtualRow
              key={item.id}
              device={item.device}
              position={positions[item.id]}
              isSelected={isDeviceSelected}
              groups={groupsList}
              isHidden={hiddenSet.has(item.id)}
              onToggleVisibility={handleToggleVisibility}
              rowRef={selectedRowRef}
              index={virtualRow.index}
              totalCount={deviceCount}
              className={classes.virtualRow}
              style={{
                height: virtualRow.size,
                transform: `translateY(${virtualRow.start}px)`,
              }}
            />
          );
        })}
      </div>
    </div>
  );

  if (standalone) {
    return (
      <div className={classes.panelRoot}>
        {listContent}
        <GroupEditModal open={groupModalOpen} onClose={handleCloseGroupModal} group={editingGroup} />
      </div>
    );
  }
  return (
    <>
      {listContent}
      <GroupEditModal open={groupModalOpen} onClose={handleCloseGroupModal} group={editingGroup} />
    </>
  );
};

export default DeviceList;
